/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ChartListData,
  DetailTableData,
  OverallData,
  OverallTable,
} from 'components/templates/AcTransactionHistory/chart';
import matchingHistoryDataService from 'services/analyticsCenter/matchingHistory';
import {
  MatchingHistoryTypes,
  MatchingHistoryParamsTypes,
  ItemMatchingHistoryTypes,
} from 'services/analyticsCenter/matchingHistory/types';
import {
  getPriceHistoriesService,
  getShareholderTransactionsService,
} from 'services/analyticsCenter/transactionHistory';
import {
  ChartVolumeAndPriceDataTypes,
  GetTHShareholderTransactionsParamsTypes,
  PriceHistoriesParamsTypes,
  PriceHistoriesTypes,
  THShareholderTransactionsTypes,
} from 'services/analyticsCenter/transactionHistory/types';
import getWsPriceboardService from 'services/wsPriceboard';
import {
  getHourMinuteFromTime,
  getTimeTodayWithTime,
  parseStringToNumber,
  renderValue,
} from 'utils/functions';

type TransactionHistoryState = {
  priceHistories?: MatchingHistoryTypes;
  shareholderTransactions?: PaginationData<THShareholderTransactionsTypes[]>;
  orderMatchingHistory?: ChartListData;
  orderMatchingLoading: boolean;
};

const initialState: TransactionHistoryState = {
  priceHistories: undefined,
  shareholderTransactions: undefined,
  orderMatchingHistory: undefined,
  orderMatchingLoading: false,
};

export const getPriceHistoriesAction = createAsyncThunk<
  PaginationData<PriceHistoriesTypes[]>,
  PriceHistoriesParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'transactionHistoryReducer/getPriceHistoriesAction',
  async (params: PriceHistoriesParamsTypes, { rejectWithValue }) => {
    try {
      return await getPriceHistoriesService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getOtherMatchingHistoryTodayAction = createAsyncThunk<
  ItemMatchingHistoryTypes[],
  MatchingHistoryParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'transactionHistoryReducer/getOtherMatchingHistoryToday',
  async (params: MatchingHistoryParamsTypes, { rejectWithValue }) => {
    try {
      return await matchingHistoryDataService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getShareholderTransactionsAction = createAsyncThunk<
  PaginationData<THShareholderTransactionsTypes[]>,
  GetTHShareholderTransactionsParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'transactionHistoryReducer/getShareholderTransactionsAction',
  async (
    params: GetTHShareholderTransactionsParamsTypes,
    { rejectWithValue },
  ) => {
    try {
      return await getShareholderTransactionsService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getOrderMatchingHistoryAction = createAsyncThunk<
  SocketResponseTypes,
  string,
  { rejectValue: ErrorResponse }
>(
  'transactionHistoryReducer/getOrderMatchingHistoryAction',
  async (stockCode, { rejectWithValue }) => {
    try {
      return await getWsPriceboardService({
        selectedStocks: stockCode,
        criteriaId: '',
        marketId: 0,
        lastSeq: 0,
        isReqTL: true,
        isReqMK: false,
        tlSymbol: stockCode,
        pthMktId: '',
        isGetNullValue: false,
        isIncrValue: true,
      });
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

const reduceOverallTableData = (
  curentData: OverallTable[],
  data: OverallTable[],
) => {
  const resultData: OverallTable[] = [...curentData, ...data];
  const sumVolume = [...curentData, ...data].reduce(
    (a, b) => a + Number(b.amount),
    0,
  );
  const result = resultData
    .map((item) => ({ ...item, amount: item.amount }))
    .reduce(
      (oldGroups, item) => ({
        ...oldGroups,
        [item.price]: {
          ...item,
          ...oldGroups[item.price],
          amount: (Number(oldGroups[item.price]?.amount || 0) + Number(item.amount)).toString(),
        },
      }),
      {} as Record<string, OverallTable>,
    );
  return Object.values(result).map((item) => ({
    ...item,
    rate: renderValue((Number(item.amount) / sumVolume) * 100),
  })).sort((a, b) => Number(b.price) - Number(a.price));
};

const socketUpdateOverallTable = (
  oldData: OverallTable[], newData: OverallTable[],
) => {
  const sumVolume = [...oldData, ...newData].reduce(
    (a, b) => a + Number(b.amount),
    0,
  );
  const result = oldData.slice();
  newData.forEach((element) => {
    const index = result.findIndex((item) => item.price === element.price);
    if (index > -1) {
      const newAmount = (Number(result[index].amount) + Number(element.amount));
      result[index] = {
        ...result[index],
        amount: newAmount.toString(),
        rate: renderValue((Number(newAmount) / sumVolume) * 100),
      };
    } else {
      result.push(element);
    }
  });
  return result.sort((a, b) => Number(b.price) - Number(a.price));
};

const reduceDetailData = (data: DetailTableData[]) => {
  const result = data
    .sort((a, b) => getTimeTodayWithTime(b.time) - getTimeTodayWithTime(a.time))
    .map((item) => ({ ...item, time: getHourMinuteFromTime(item.time) }))
    .reduce(
      (oldGroups, item) => ({
        ...oldGroups,
        [item.time]: {
          ...item,
          ...oldGroups[item.time],
          amount: (
            Number(oldGroups[item.time]?.amount || 0) + Number(item.amount)
          ).toString(),
        },
      }),
      {} as Record<string, DetailTableData>,
    );

  return Object.values(result).map((item) => ({
    ...item,
    rate: ((Number(item.amount) / Number(item.amountSaved)) * 100).toString(),
  }));
};

const socketUpdateDetailData = (
  data: DetailTableData[],
  newData: DetailTableData[],
) => {
  const newDataReduce = reduceDetailData(newData);
  const [head, ...tail] = data;
  if (head && head.time !== newDataReduce[newDataReduce.length - 1].time) {
    return [...newDataReduce, ...data];
  }
  return [...newDataReduce, ...tail];
};

export const transactionHistorySlice = createSlice({
  name: 'transactionHistoryReducer',
  initialState,
  reducers: {
    resetData: ($state) => {
      $state.orderMatchingHistory = undefined;
    },
    socketUpdatePrice: (
      $state,
      action: PayloadAction<{
        price: number;
        priceChangePercent?: number;
        priceChange: number;
      }>,
    ) => {
      if ($state.priceHistories) {
        const clone = $state.priceHistories.items.slice();
        clone[0] = {
          ...clone[0],
          price: action.payload.price,
          priceChange: action.payload.priceChange,
          priceChangePercent:
            action.payload.priceChangePercent || clone[0].priceChangePercent,
        };
        $state.priceHistories = { ...$state.priceHistories, items: clone };
      }
    },
    socketUpdateOrderMatching: (
      $state,
      action: PayloadAction<{
        newOverall: OverallData;
        newOverallItemTable: OverallTable[];
        newItemDetail: DetailTableData[];
        newChartItem: ChartVolumeAndPriceDataTypes[];
      }>,
    ) => {
      $state.orderMatchingHistory = {
        overallData: action.payload.newOverall,
        overallDataTable: socketUpdateOverallTable(
          $state.orderMatchingHistory?.overallDataTable || [],
          action.payload.newOverallItemTable,
        ),
        detailData: socketUpdateDetailData(
          $state.orderMatchingHistory?.detailData || [],
          action.payload.newItemDetail,
        ),
        dataChart: action.payload.newChartItem.concat(
          $state.orderMatchingHistory?.dataChart || [],
        ),
      };
    },
  },
  extraReducers(builder) {
    builder.addCase(
      getOtherMatchingHistoryTodayAction.fulfilled,
      ($state, action) => {
        $state.priceHistories = { items: action.payload };
      },
    );
    builder.addCase(
      getShareholderTransactionsAction.fulfilled,
      ($state, action) => {
        $state.shareholderTransactions = action.payload;
      },
    );
    builder.addCase(getOrderMatchingHistoryAction.pending, ($state) => {
      $state.orderMatchingLoading = true;
    });
    builder.addCase(
      getOrderMatchingHistoryAction.fulfilled,
      ($state, action) => {
        if (
          action.payload.pb
          && action.payload.pb.f
          && action.payload.pb.f.length
          && action.payload.tl
          && action.payload.tl.f
          && action.payload.tl.f.length
        ) {
          const stock = JSON.parse(action.payload.pb.f[0]) as StockInfoTypes;
          const tlfData = action.payload.tl.f;
          const lastest = tlfData.length - 1;
          const pricePercent = (parseStringToNumber(tlfData[lastest].f.i[2])
              / parseStringToNumber(stock.RE))
            * 100;
          const newOveralData = {
            reference: renderValue(parseStringToNumber(stock.RE)),
            min: renderValue(parseStringToNumber(stock.Z)),
            max: renderValue(parseStringToNumber(stock.Y)),
            open: renderValue(parseStringToNumber(stock.X)),
            current: renderValue(parseStringToNumber(tlfData[lastest].f.i[1])),
            percent: `${renderValue(
              parseStringToNumber(tlfData[lastest].f.i[2]),
            )} (${renderValue(pricePercent)}%)`,
            increase: parseStringToNumber(tlfData[lastest].f.i[2]) >= 0,
            total: renderValue(parseStringToNumber(stock.Q)),
          };

          // OverallTableData
          const newOverallTableData = tlfData.map((item) => ({
            price: parseStringToNumber(item.f.i[1]).toString(),
            amount: parseStringToNumber(item.f.i[3]).toString(),
            rate: renderValue(
              parseStringToNumber(
                `${(item.f.i[3] / parseStringToNumber(stock.Q)) * 100}`,
              ),
            ),
          }));

          // DetailTable
          const newDetailData = tlfData.map((item) => ({
            time: item.f.i[0],
            price: renderValue(parseStringToNumber(item.f.i[1])),
            amount: renderValue(parseStringToNumber(item.f.i[3])),
            amountSaved: item.f.i[4],
            percent: `${item.f.i[2]} (${renderValue((parseStringToNumber(item.f.i[2])
              / parseStringToNumber(stock.RE))
            * 100)}%)`,
            increase: parseStringToNumber(item.f.i[2]) >= 0,
            rate: renderValue(
              parseStringToNumber(
                `${(item.f.i[3] / parseStringToNumber(stock.Q)) * 100}`,
              ),
            ),
          }));

          // chart
          const dataConvert = tlfData.map((item) => ({
            price: parseStringToNumber(item.f.i[1]),
            volume: parseStringToNumber(item.f.i[3]),
            priceChangePercent: parseStringToNumber(stock.Q),
            priceChange: parseStringToNumber(item.f.i[2]),
            sessionDate: new Date(
              getTimeTodayWithTime(item.f.i[0]),
            ).toISOString(),
          }));

          $state.orderMatchingHistory = {
            overallData: newOveralData,
            overallDataTable: reduceOverallTableData([], newOverallTableData),
            detailData: reduceDetailData(newDetailData),
            dataChart: dataConvert,
          };
        }
        $state.orderMatchingLoading = false;
      },
    );
    builder.addCase(getOrderMatchingHistoryAction.rejected, ($state) => {
      $state.orderMatchingLoading = false;
    });
  },
});

export const {
  resetData,
  socketUpdatePrice,
  socketUpdateOrderMatching,
} = transactionHistorySlice.actions;

export default transactionHistorySlice.reducer;
